// dataSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import baseUrl from './baseUrl';

// Define an initial state
const initialState = {
    totalViewersCount: 0,
    totalAdvertisersCount: 0,
    totalApprovedAds: 0,
    totalRejectedAds: 0,
    totalAmountSpent: 0,

    status: 'idle',
    error: null,
    user: '',
    userToEdit: [],
    viewersList: [],
    advertisersList: [],
    adminList: [],
    roleWiseDetails: [],
    search: [],
    postingCharges: []
};

export const fetchAdminDetails = createAsyncThunk(
    'data/fetchAdminDetails',
    async userId => {
        const response = await axios.get(`${baseUrl}/users/getById/${userId}`);

        return response.data;
    }
);

export const fetchAllViewersList = createAsyncThunk(
    'data/fetchAllViewersList',
    async currentPage => {
        const response = await axios.get(
            `${baseUrl}/users/getViewerList/${currentPage}`
        ); // Replace with your API endpoint
        // console.log('response, fetchAllViewersList', response);
        return response.data;
    }
);

export const fetchAllAdvertisersList = createAsyncThunk(
    'data/fetchAllAdvertisersList',
    async currentPage => {
        const response = await axios.get(
            `${baseUrl}/users/getAdvertiserList/${currentPage}`
        );

        return response.data;
    }
);

export const fetchTotalAdvertisersCount = createAsyncThunk(
    'data/fetchTotalAdvertisersCount',
    async userId => {
        const response = await axios.get(`${baseUrl}/users/countAdvertiser`);

        return response.data;
    }
);

export const fetchAllAdminList = createAsyncThunk(
    'data/fetchAllAdminList',
    async (role) => {
        const response = await fetch(`${baseUrl}/users/getAdmin?&role=${role}`, {
            method: 'GET',  // Use POST to send data in the body
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Failed to fetch admin list');
        }

        const data = await response.json();
        return data[role].users;
    }
);


export const fetchTotalViewersCount = createAsyncThunk(
    'data/fetchTotalViewersCount',
    async userId => {
        const response = await axios.get(`${baseUrl}/users/countViewer`);
        return response.data;
    }
);

export const fetchTotalApprovedAds = createAsyncThunk(
    'data/fetchTotalApprovedAds',
    async userId => {
        const response = await axios.get(
            `${baseUrl}/ads/getApprovedCountForAdmin`
        );
        return response.data;
    }
);

export const fetchTotalRejectedAds = createAsyncThunk(
    'data/fetchTotalRejectedAds',
    async userId => {
        const response = await axios.get(
            `${baseUrl}/ads/getRejectedCountForAdmin`
        );
        return response.data;
    }
);

export const fetchTotalAmountSpent = createAsyncThunk(
    'data/fetchTotalAmountSpent',
    async userId => {
        const response = await axios.get(`${baseUrl}/ads/totalSpent`);
        return response.data;
    }
);

export const getPostingCharges = createAsyncThunk(
    'data/getPostingCharges',
    async userId => {
        const response = await axios.get(`${baseUrl}/price/getAll`);
        return response.data;
    }
);

export const updatePostingCharge = createAsyncThunk(
    'data/updatePostingCharge',
    async ({ userId, data }) => {
        const response = await axios.put(`${baseUrl}/price/update/${userId}/${data.id}`, data);
        return response.data;
    }
);

export const fetchDetailsByRole = createAsyncThunk(
    'data/fetchDetailsByRole',
    async role => {
        const response = await axios.get(`${baseUrl}/access/getAccess/${role}`);
        return response.data;
    }
);

export const searchViewerList = createAsyncThunk(
    'users/searchViewerList',
    async searchText => {
        const response = await axios.get(
            `${baseUrl}/users/searchAll?role=Viewer&searchTerm=${searchText.trim()}`
        );
        return response.data;
    }
);

export const searchAdvertiserList = createAsyncThunk(
    'users/searchAdvertiserList',
    async searchText => {
        const response = await axios.get(
            `${baseUrl}/users/searchAll?role=Advertiser&searchTerm=${searchText.trim()}`
        );
        return response.data;
    }
);

// Create a slice
const userSlice = createSlice({
    name: 'data',
    initialState,

    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchAdminDetails.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchAdminDetails.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.user = action.payload;
            })
            .addCase(fetchAdminDetails.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchAllViewersList.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchAllViewersList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.viewersList = action.payload;
            })
            .addCase(fetchAllViewersList.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchAllAdvertisersList.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchAllAdvertisersList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.advertisersList = action.payload;
            })
            .addCase(fetchAllAdvertisersList.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchAllAdminList.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchAllAdminList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.adminList = action.payload;
            })
            .addCase(fetchAllAdminList.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchTotalViewersCount.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchTotalViewersCount.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.totalViewersCount = action.payload.count;
            })
            .addCase(fetchTotalViewersCount.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchTotalAdvertisersCount.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchTotalAdvertisersCount.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.totalAdvertisersCount = action.payload.count;
            })
            .addCase(fetchTotalAdvertisersCount.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchTotalApprovedAds.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchTotalApprovedAds.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.totalApprovedAds = action.payload;
            })
            .addCase(fetchTotalApprovedAds.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchTotalRejectedAds.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchTotalRejectedAds.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.totalRejectedAds = action.payload;
            })
            .addCase(fetchTotalRejectedAds.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchTotalAmountSpent.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchTotalAmountSpent.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.totalAmountSpent = action.payload;
            })
            .addCase(fetchTotalAmountSpent.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(getPostingCharges.pending, state => {
                state.status = 'loading';
            })
            .addCase(getPostingCharges.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.postingCharges = action.payload;
            })
            .addCase(getPostingCharges.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(updatePostingCharge.pending, (state) => {
                // We don't set global loading status here to avoid full page loader
                state.error = null;
            })
            .addCase(updatePostingCharge.fulfilled, (state, action) => {
                const index = state.postingCharges.findIndex(charge => charge.id === action.payload.id);
                if (index !== -1) {
                    state.postingCharges[index] = action.payload;
                }
            })
            .addCase(updatePostingCharge.rejected, (state, action) => {
                state.error = action.error.message;
            });
        builder
            .addCase(fetchDetailsByRole.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchDetailsByRole.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.roleWiseDetails = action.payload;
            })
            .addCase(fetchDetailsByRole.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(searchViewerList.pending, state => {
                state.status = 'loading';
            })
            .addCase(searchViewerList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.search = action.payload;
            })
            .addCase(searchViewerList.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(searchAdvertiserList.pending, state => {
                state.status = 'loading';
            })
            .addCase(searchAdvertiserList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.search = action.payload;
            })
            .addCase(searchAdvertiserList.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
    }
});

export default userSlice.reducer;
